import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../lib/api";
import Logo from "../components/Logo";
import { PublicPacketSection } from "../components/carrier/PublicPacketSection";
import { Truck, MapPin, Trophy, CheckCircle, Warning, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * CarrierPublicPage — the public marketing page for a fleet at
 * /carriers/{slug}. Purpose: turn documented SLA performance into a
 * sales asset that (a) brokers can share with shippers and (b) Google
 * indexes for lane-specific searches.
 *
 * SEO: sets document.title + meta description on data load. Keep the
 * page fast (single API call, no lazy chunks) so PageSpeed loves it.
 */
export default function CarrierPublicPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await client.get(`/public/carriers/${slug}`);
        if (!alive) return;
        setData(d);
        // SEO meta — populate on mount for indexers that don't run JS.
        // At worst this is a no-op (indexer executes JS anyway).
        document.title = `${d.company_name} · On-time delivery record | FreightPilot AI`;
        const desc = d.hero?.on_time_pct_90d != null
          ? `${d.company_name} delivered ${d.hero.on_time_pct_90d}% on-time across ${d.hero.delivered_90d} loads in the last 90 days. Documented SLA performance for shipper due diligence.`
          : `${d.company_name} on-time delivery record — documented SLA performance for shipper due diligence.`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.content = desc;
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Carrier not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center p-6" data-testid="carrier-public-error">
        <Logo />
        <h1 className="font-display text-2xl sm:text-3xl mt-6 mb-2">Carrier not found</h1>
        <p className="text-neutral-400 text-sm text-center max-w-md">The public page for <span className="font-mono">{slug}</span> isn't published yet.</p>
        <Link to="/" className="text-[#FF5A1F] hover:underline text-sm mt-4">← Back to FreightPilot AI</Link>
      </div>
    );
  }

  const pct = data.hero?.on_time_pct_90d;
  const pctDisplay = pct == null ? "—" : `${pct}%`;
  const pctColor = pct == null
    ? "text-neutral-500"
    : pct >= 90 ? "text-emerald-400" : pct >= 75 ? "text-amber-400" : "text-red-400";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="carrier-public-page">
      {/* Header with FreightPilot brand + CTA */}
      <header className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Logo />
          <a
            href={data.cta_url || "https://freightpilotai.ai"}
            className="text-xs text-neutral-400 hover:text-white transition-colors inline-flex items-center gap-1"
          >
            Powered by FreightPilot AI <ArrowRight size={12} />
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-20">
        {/* Hero */}
        <div className="mb-16">
          <div className="pill pill-primary mb-4 inline-flex items-center gap-1.5">
            <Truck size={11} weight="fill" /> ON-TIME RECORD · LAST 90 DAYS
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-4">
            {data.company_name}
          </h1>
          <p className="text-base sm:text-lg text-neutral-400 max-w-2xl leading-relaxed">
            Documented service-level performance — the numbers your shipper wants before they book with a new carrier.
          </p>
        </div>

        <PublicPacketSection slug={slug} />

        {/* Big on-time % */}
        <div className="card-surface p-8 sm:p-12 mb-8" data-testid="carrier-hero-stat">
          <div className="overline text-[#FF5A1F] mb-3">On-Time Delivery</div>
          <div className={`font-display text-7xl sm:text-8xl font-bold leading-none tracking-tight ${pctColor}`}>
            {pctDisplay}
          </div>
          <div className="text-neutral-400 text-sm mt-4">
            {data.hero?.delivered_90d ?? 0} loads delivered · verified via ELD GPS + broker confirmations
          </div>
        </div>

        {/* Top lanes */}
        {data.lanes?.length > 0 && (
          <div className="mb-8">
            <div className="overline mb-4 flex items-center gap-1.5"><MapPin size={11} weight="fill" /> Top Lanes</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.lanes.map((lane, i) => (
                <div key={i} data-testid={`carrier-lane-${i}`} className="card-surface p-4 sm:p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-mono text-sm text-white">{lane.lane}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{lane.count} loads</div>
                  </div>
                  <div className={`font-mono text-lg font-bold ${lane.on_time_pct >= 90 ? "text-emerald-400" : lane.on_time_pct >= 75 ? "text-amber-400" : "text-red-400"}`}>
                    {lane.on_time_pct}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Best driver spotlight */}
        {data.best_driver && (
          <div className="card-surface p-6 sm:p-8 mb-8" data-testid="carrier-best-driver">
            <div className="overline mb-2 flex items-center gap-1.5"><Trophy size={11} weight="fill" /> Top Performer</div>
            <div className="flex items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
                <CheckCircle size={22} weight="fill" />
              </div>
              <div>
                <div className="font-display text-xl text-white">{data.best_driver.name_first}</div>
                <div className="text-sm text-neutral-400 mt-0.5">
                  {data.best_driver.on_time_pct}% on-time across {data.best_driver.delivered} loads
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA to book */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5"><Sparkle size={11} weight="fill" /> Want your own tracked page?</div>
            <div className="text-sm text-neutral-300">Every FreightPilot carrier gets a public performance page — 7-day free trial.</div>
          </div>
          <a
            href={data.cta_url || "https://freightpilotai.ai"}
            className="btn-primary rounded-full px-5 py-3 inline-flex items-center gap-1.5 text-sm"
            data-testid="carrier-cta-btn"
          >
            Start Free Trial <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </main>

      <footer className="border-t border-white/5 py-6">
        <div className="max-w-5xl mx-auto px-6 text-[11px] text-neutral-500 text-center">
          FreightPilot AI™ · SLA data updated {data.updated_at ? new Date(data.updated_at).toLocaleDateString() : "recently"}. Powered by ELD GPS + broker confirmations.
        </div>
      </footer>
    </div>
  );
}
